/**
 * @file SpotPricing.js
 * @version 1.5.4
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-11
 * @modified 2026-09-29
 * @description Spot Pricing for Customers
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { setContractsFulfilled, fulfilledAction } from '../utils/contractFulfilment';
import './SearchLayout.css';
import './TableStyles.css';
import './ContractPricing.css';
import './ResizableColumns.css';
import Dropdown from './Dropdown';
import Input from './Input';
import DateInput from './DateInput';
import PaginationControls from './PaginationControls';
import { ScopeLinkBadge } from './PricingBadges';
import ContractPricingModal from './ContractPricingModal';
import ContactModal from './ContactModal';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import ReactDOM from 'react-dom';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  search: '', rep: '', mill: '', fromDate: '', toDate: '',
  limit: '20'
};

function SpotPricing() {
  const [contracts, setContracts] = useState([]);
  const [reps, setReps] = useState([]);
  const [mills, setMills] = useState([]);
  const [loading, setLoading] = useState(false);

  // Slideout buttons
  useSlideOutButton();
  // Drag-to-resize columns (9 cols: customer, product, supplier, from, to,
  // price, margin, rep, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('spotPricing', 9);
  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedContactId, setSelectedContactId] = usePersistedState('spotPricing.contactModalId', null);
  const [modalMode, setModalMode] = useState('edit');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [filters, setFilters] = usePersistedState('spotPricing.filters', {
    search: '',
    rep: '',
    mill: '',
    fromDate: '',
    toDate: '',
    limit: '20'
  });
  const [sorting, setSorting] = usePersistedState('spotPricing.sorting', {
    field: '',
    direction: 'asc'
  });
  const [pagination, setPagination] = usePersistedState('spotPricing.pagination', {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const CATEGORY_ID = 5; // Spot

  useEffect(() => {
    loadReps();
    loadMills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      loadContracts();
    }, 300);
    return () => clearTimeout(delayTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.fromDate, filters.toDate, filters.limit]);

  const loadReps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/distinct/reps?category_id=${CATEGORY_ID}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setReps(data);
    } catch (error) {
      console.error('Error loading reps:', error);
    }
  };

  const loadMills = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/distinct/suppliers?category_id=${CATEGORY_ID}`, {
        credentials: 'include'
      });
      const suppliers = await response.json();
      setMills(suppliers);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadContracts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        category_id: CATEGORY_ID,
        limit: 10000
      });

      if (filters.fromDate) {
        params.append('from_date', filters.fromDate);
      }
      if (filters.toDate) {
        params.append('to_date', filters.toDate);
      }

      const response = await fetch(`${API_URL}/api/pricing/contracts?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      const allContracts = data.data || [];
      setContracts(allContracts);

      const limit = parseInt(filters.limit) || 50;
      setPagination(prev => ({
        ...prev,
        page: 1,
        limit: limit,
        total: allContracts.length,
        totalPages: Math.ceil(allContracts.length / limit)
      }));
    } catch (error) {
      console.error('Error loading contracts:', error);
      setContracts([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Filter, sort, and paginate contracts
  const filteredAndSortedContracts = contracts
    .filter(contract => {
      if (filters.search) {
        const matchesSearch = matchesAllTerms([
          contract.company_name,
          contract.account_no,
          contract.product_name,
          contract.product_code,
          contract.contract_number,
          // Searchable, though this table has no contract column at all —
          // contract_number was already searched the same way. No spot
          // contract carries a supplier reference today.
          contract.supplier_contract_number,
        ], filters.search);
        if (!matchesSearch) return false;
      }

      if (filters.rep && contract.rep_name !== filters.rep) {
        return false;
      }

      if (filters.mill && contract.supplier_contact_id?.toString() !== filters.mill) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (!sorting.field) return 0;

      let aVal = a[sorting.field];
      let bVal = b[sorting.field];

      if (['quantity', 'cost', 'price', 'margin'].includes(sorting.field)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (['start_date', 'end_date'].includes(sorting.field)) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Update pagination when filters or sorting changes
  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev,
      page: 1,
      limit: limit,
      total: filteredAndSortedContracts.length,
      totalPages: Math.ceil(filteredAndSortedContracts.length / limit)
    }));
  }, [filteredAndSortedContracts.length, filters.search, filters.rep, filters.mill, filters.limit, sorting.field, sorting.direction]);

  // Paginate the filtered and sorted results
  const paginatedContracts = filteredAndSortedContracts.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  // No selection on this table, so no selectedIds/setSelectedIds and no bulk group.
  const ctxMenu = useTableContextMenu({
    rows: paginatedContracts,
    getRowId: (c) => c.id
  });

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
    setSorting({ field: '', direction: 'asc' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any header filter on lights the Clear Filters button. From/To are passed
  // under the names activeFilters treats as one date filter.
  const headerFilters = activeHeaderFilters(
    { ...filters, dateFrom: filters.fromDate, dateTo: filters.toDate },
    { ...CLEARED_FILTERS, dateFrom: '', dateTo: '' },
    { ignore: ['fromDate', 'toDate'] }
  );
  const filtersApplied = headerFilters.count > 0;

  const handleAddNew = () => {
    setSelectedContract(null);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleView = (contract) => {
    setSelectedContract(contract);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (contract) => {
    setSelectedContract(contract);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (contract) => {
    setConfirmDialog({
      isOpen: true,
      message: `Are you sure you want to delete the pricing for ${contract.company_name} - ${contract.product_name}?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await fetch(`${API_URL}/api/pricing/contracts/${contract.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });

          if (response.ok) {
            loadContracts();
          } else {
            setAlertDialog({ isOpen: true, message: 'Failed to delete pricing entry', type: 'error' });
          }
        } catch (error) {
          console.error('Error deleting pricing:', error);
          setAlertDialog({ isOpen: true, message: 'Error deleting pricing entry', type: 'error' });
        }
      }
    });
  };

  const handleSave = async () => {
    setShowModal(false);
    await loadContracts();
  };

  const handleRefreshCost = async (contract) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/refresh-cost`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (data.skipped) {
        setAlertDialog({ isOpen: true, message: data.reason || 'No purchase_unit_price on inventory item', type: 'warning' });
        return;
      }
      await loadContracts();
      setAlertDialog({ isOpen: true, message: `Cost refreshed to ${formatPrice(data.newCost ?? 0)}`, type: 'success' });
    } catch (error) {
      console.error('Error refreshing cost:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to refresh cost', type: 'error' });
    }
  };

  // Shared by the row's kebab menu and its right-click context menu.
  // Mark / unmark fulfilled from the row menus. A grouped contract changes as a
  // whole on the server.
  const handleSetFulfilled = async (ids, isFulfilled) => {
    try {
      await setContractsFulfilled(ids, isFulfilled);
      await loadContracts();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: `Failed to ${isFulfilled ? 'mark' : 'unmark'} the contract fulfilled: ${e.message}`, type: 'error' });
    }
  };

  const buildRowActions = (contract) => [
    { key: 'view', icon: 'visibility', label: 'View', title: 'View', onClick: () => handleView(contract) },
    { key: 'edit', icon: 'edit', label: 'Edit', title: 'Edit', onClick: () => handleEdit(contract) },
    fulfilledAction({ fulfilled: !!contract.is_fulfilled_override, onToggle: () => handleSetFulfilled([contract.id], !contract.is_fulfilled_override) }),
    { key: 'refresh', icon: 'sync', label: 'Refresh cost', title: 'Refresh cost from inventory', onClick: () => handleRefreshCost(contract) },
    { key: 'delete', icon: 'delete', label: 'Delete', title: 'Delete', className: 'action-danger', onClick: () => handleDelete(contract) },
  ];

  return (
    <div className="contract-pricing spot">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Manage customer spot pricing
          </span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Set a price for any customer and product for a custom date range</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters" style={{ paddingBottom: 'var(--spacing-md' }}>
        <div className="filter-row" style={{ gridTemplateColumns: "1fr 100px 100px 175px 140px 70px" }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'product', 'product code', 'contract no. (ours and the supplier\'s)']} />
            </label>
            <Input
              type="text"
              placeholder="Customer, account #, product ..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>From Date</label>
            <DateInput
              value={filters.fromDate}
              onChange={(value) => handleFilterChange('fromDate', value)}
              placeholder="From date"
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <DateInput
              value={filters.toDate}
              onChange={(value) => handleFilterChange('toDate', value)}
              placeholder="To date"
            />
          </div>

          <div className="filter-group">
            <label>Supplier</label>
            <Dropdown
              value={filters.mill}
              onChange={(value) => handleFilterChange('mill', value)}
              options={[
                { value: '', label: 'All Suppliers' },
                ...mills.map(supplier => ({
                  value: supplier.id.toString(),
                  label: supplier.company_name
                }))
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Rep</label>
            <Dropdown
              value={filters.rep}
              onChange={(value) => handleFilterChange('rep', value)}
              options={[
                { value: '', label: 'All Reps' },
                ...reps.map(rep => ({
                  value: rep,
                  label: rep
                }))
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="20"
            />
          </div>

        </div>
      </div>

      <div className="table-toolbar">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          <ResetButton />
        </div>
        {pagination.totalPages > 0 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={changePage}
          />
        )}
      </div>

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <table className="data-table resizable-table" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('company_name')}>
                  Customer {sorting.field === 'company_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="sortable" onClick={() => handleSort('product_name')}>
                  Product {sorting.field === 'product_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={1} />
                </th>
                <th className="sortable" onClick={() => handleSort('mill')}>
                  Supplier {sorting.field === 'mill' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th className="sortable" onClick={() => handleSort('start_date')}>
                  From {sorting.field === 'start_date' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th className="sortable" onClick={() => handleSort('end_date')}>
                  To {sorting.field === 'end_date' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('price')}>
                  Price {sorting.field === 'price' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('margin')}>
                  Margin {sorting.field === 'margin' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={6} />
                </th>
                <th className="sortable" onClick={() => handleSort('rep_name')}>
                  Rep {sorting.field === 'rep_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={7} />
                </th>
                <th className="actions-col"><ResizeHandle colIndex={8} /></th>
              </tr>
            </thead>
            <tbody>
              {paginatedContracts.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    No spot prices found
                  </td>
                </tr>
              ) : (
                paginatedContracts.map((contract) => (
                  <tr
                    key={contract.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleEdit(contract)}
                    onContextMenu={ctxMenu.onRowContextMenu(contract)}
                  >
                    <td>
                      <span className="name-with-badge">
                        {contract.account_no && (
                          <span
                            className="account-badge-inline"
                            style={contract.account_id ? { cursor: 'pointer' } : undefined}
                            onClick={contract.account_id ? (e) => { e.stopPropagation(); setSelectedContactId(contract.account_id); } : undefined}
                          >
                            {contract.account_no}
                          </span>
                        )}
                        {contract.company_name ? (
                          <span
                            className="contact-link"
                            onClick={(e) => { e.stopPropagation(); if (contract.account_id) setSelectedContactId(contract.account_id); }}
                          >
                            {contract.company_name}
                          </span>
                        ) : (
                          <span>-</span>
                        )}
                      </span>
                      <span className="address-with-space">
                        <span className="scope-link-slot"><ScopeLinkBadge scopeKind={contract.scope_kind} /></span>
                        {contract.address_line && <span className="delivery-address">{contract.address_line}</span>}
                      </span>
                    </td>
                    <td>
                      <div>
                        {contract.product_name}
                        {contract.is_holding_product && (
                          <span className="option-status-badge is-holding" title="Holding product — edit the price to choose the item once it's set up">HOLDING</span>
                        )}
                        {contract.additive_name && (
                          <span style={{ color: 'var(--text-secondary)' }}> + {contract.additive_name}</span>
                        )}
                      </div>
                      {(contract.product_code || contract.additive_code) && (
                        <div className="product-code">
                          {contract.product_code || ''}
                          {contract.product_code && contract.additive_code && ' '}
                          {contract.additive_code && `(${contract.additive_code})`}
                        </div>
                      )}
                    </td>
                    <td>{contract.supplier_name || contract.mill || '-'}</td>
                    <td>{formatDate(contract.start_date)}</td>
                    <td>{formatDate(contract.end_date)}</td>
                    <td className="text-right">
                      <div className="price">{formatPrice(contract.price)}</div>
                      {contract.cost && (
                        <div className="cost">{'('}{formatPrice(contract.cost)}{')'}</div>
                      )}
                    </td>
                    <td className="text-right">{formatCurrency(contract.margin)}</td>
                    <td>{contract.rep_name || '-'}</td>
                    <td className="actions-col" style={{ textAlign: "center", verticalAlign: "middle" }} onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu actions={buildRowActions(contract)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'reload', icon: 'refresh', label: 'Reload spot prices', onClick: loadContracts },
          { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters }
        ]}
      />

      {showModal && (
        <ContractPricingModal
          contract={selectedContract}
          categoryId={CATEGORY_ID}
          mode={modalMode}
          onClose={() => { setShowModal(false); loadContracts(); }}
          onSave={handleSave}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />,
        document.body
      )}
    </div>
  );
}

export default SpotPricing;
